import {
  GET_CLASS_TEACHER_OVERVIEW_USE_CASE,
  LIST_CLASS_STUDENT_SUBMISSIONS_USE_CASE,
  ClassTeacherOverview,
  ClassStudentAttemptPage,
} from '@module/class';
import { Action, createReducer } from '@lib/plugin-redux-core';
import { UPLOAD_CLASS_COVER_USE_CASE } from '@module/file-storage';
import {
  ARCHIVE_CLASS_USE_CASE,
  ACTIVATE_CLASS_USE_CASE,
  DELETE_CLASS_USE_CASE,
  LIST_ASSIGNMENT_CLASSES_USE_CASE,
  ClassStudent,
  ClassSummary,
  ClassTeacher,
  CREATE_CLASS_USE_CASE,
  GET_CLASS_USE_CASE,
  LIST_CLASSES_USE_CASE,
  LIST_CLASS_STUDENTS_USE_CASE,
  LIST_CLASS_TEACHERS_USE_CASE,
  UPDATE_CLASS_USE_CASE,
  REMOVE_CLASS_STUDENT_USE_CASE,
  REMOVE_CLASS_TEACHER_USE_CASE,
  ClassInvitation,
  GET_CLASS_INVITATION_USE_CASE,
  GET_CLASS_INVITATION_BY_CODE_USE_CASE,
  JOIN_CLASS_AS_TEACHER_USE_CASE,
  LEAVE_CLASS_USE_CASE,
} from '@module/class';
import { GET_USERS_USE_CASE, UserSummary } from '@module/user';
import { AssignmentSummary } from '@module/assignment';
import { GET_CLASS_DASHBOARD_ASSIGNMENTS_USE_CASE } from './class-dashboard.constants';

export type ClassesState = {
  mutationError: boolean;
  teacherOverview: {
    classId: string | null;
    data: ClassTeacherOverview | null;
    loading: boolean;
    error: string | null;
  };
  studentAttempts: {
    requestId: string | null;
    data: ClassStudentAttemptPage | null;
    loading: boolean;
    error: string | null;
  };
  coverUpload: {
    requestId: string | null;
    loading: boolean;
    url: string | null;
    error: string | null;
  };
  dashboardRequest: string | null;
  dashboardAssignments: AssignmentSummary[];
  dashboardAssignmentsLoading: boolean;
  dashboardAssignmentsNextToken: string | null;
  dashboardAssignmentsError: string | null;
  assignmentOptions: ClassSummary[];
  assignmentOptionsLoading: boolean;
  assignmentOptionsNextToken: string | null;
  assignmentOptionsError: string | null;
  listError: string | null;
  detailError: string | null;
  detailRequest: string | null;
  teacherRequest: string | null;
  studentRequest: string | null;
  rosterError: string | null;
  teachersNextToken: string | null;
  studentsNextToken: string | null;
  teachersLoading: boolean;
  studentsLoading: boolean;
  listRequest: string | null;
  items: ClassSummary[];
  nextToken: string | null;
  loading: boolean;
  creating: boolean;
  lastCreatedAt: number | null;
  lastUpdatedAt: number | null;
  lastDeletedAt: number | null;
  detail: ClassSummary | null;
  teachers: ClassTeacher[];
  students: ClassStudent[];
  memberProfiles: UserSummary[];
  memberProfilesLoading: boolean;
  detailLoading: boolean;
  updating: boolean;
  rosterVersion: number;
  invitation: ClassInvitation | null;
  invitationByCode: any;
  invitationByCodeLoading: boolean;
  invitationLoading: boolean;
  joiningInvitation: boolean;
  leaving: boolean;
  lastLeftAt: number | null;
  lastInvitationJoinedAt: number | null;
};
const dashboardScope = (input: any) =>
  JSON.stringify([input?.classId, input?.status ?? input?.statuses, input?.formId, input?.search]);
const initialState: ClassesState = {
  mutationError: false,
  teacherOverview: { classId: null, data: null, loading: false, error: null },
  studentAttempts: { requestId: null, data: null, loading: false, error: null },
  coverUpload: { requestId: null, loading: false, url: null, error: null },
  dashboardRequest: null,
  dashboardAssignments: [],
  dashboardAssignmentsLoading: false,
  dashboardAssignmentsNextToken: null,
  dashboardAssignmentsError: null,
  assignmentOptions: [],
  assignmentOptionsLoading: false,
  assignmentOptionsNextToken: null,
  assignmentOptionsError: null,
  listError: null,
  detailError: null,
  detailRequest: null,
  teacherRequest: null,
  studentRequest: null,
  rosterError: null,
  teachersNextToken: null,
  studentsNextToken: null,
  teachersLoading: false,
  studentsLoading: false,
  listRequest: null,
  items: [],
  nextToken: null,
  loading: false,
  creating: false,
  lastCreatedAt: null,
  lastUpdatedAt: null,
  lastDeletedAt: null,
  detail: null,
  teachers: [],
  students: [],
  memberProfiles: [],
  memberProfilesLoading: false,
  detailLoading: false,
  updating: false,
  rosterVersion: 0,
  invitation: null,
  invitationByCode: null,
  invitationByCodeLoading: false,
  invitationLoading: false,
  joiningInvitation: false,
  leaving: false,
  lastLeftAt: null,
  lastInvitationJoinedAt: null,
};
export const classesReducer = createReducer(initialState, [
  {
    name: GET_CLASS_TEACHER_OVERVIEW_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      teacherOverview: {
        classId: a.payload,
        data: s.teacherOverview.classId === a.payload ? s.teacherOverview.data : null,
        loading: true,
        error: null,
      },
    }),
    success: (s: ClassesState, a: any) =>
      s.teacherOverview.classId !== a.meta?.input
        ? s
        : { ...s, teacherOverview: { ...s.teacherOverview, data: a.payload, loading: false } },
    error: (s: ClassesState, a: any) =>
      s.teacherOverview.classId !== a.meta?.input
        ? s
        : { ...s, teacherOverview: { ...s.teacherOverview, loading: false, error: 'overview' } },
  },
  {
    name: LIST_CLASS_STUDENT_SUBMISSIONS_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      studentAttempts: {
        requestId: a.payload.requestId,
        data: a.payload.nextToken ? s.studentAttempts.data : null,
        loading: true,
        error: null,
      },
    }),
    success: (s: ClassesState, a: any) =>
      s.studentAttempts.requestId !== a.meta?.input?.requestId
        ? s
        : {
            ...s,
            studentAttempts: {
              ...s.studentAttempts,
              loading: false,
              data: {
                ...a.payload,
                items: a.meta?.input?.nextToken
                  ? [
                      ...new Map(
                        [...(s.studentAttempts.data?.items ?? []), ...a.payload.items].map(
                          (item) => [item.id, item],
                        ),
                      ).values(),
                    ]
                  : a.payload.items,
              },
            },
          },
    error: (s: ClassesState, a: any) =>
      s.studentAttempts.requestId !== a.meta?.input?.requestId
        ? s
        : { ...s, studentAttempts: { ...s.studentAttempts, loading: false, error: 'attempts' } },
  },
  {
    name: UPLOAD_CLASS_COVER_USE_CASE,
    executing: (state: ClassesState, action: Action) => ({
      ...state,
      coverUpload: { requestId: action.payload.requestId, loading: true, url: null, error: null },
    }),
    success: (state: ClassesState, action: Action) =>
      state.coverUpload.requestId !== action.meta?.input?.requestId
        ? state
        : {
            ...state,
            coverUpload: { ...state.coverUpload, loading: false, url: action.payload, error: null },
          },
    error: (state: ClassesState, action: Action) =>
      state.coverUpload.requestId !== action.meta?.input?.requestId
        ? state
        : {
            ...state,
            coverUpload: {
              ...state.coverUpload,
              loading: false,
              error: 'Could not upload class image.',
            },
          },
  },
  {
    name: ACTIVATE_CLASS_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, updating: true, mutationError: false }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      detail: s.detailRequest === a.payload.id ? a.payload : s.detail,
      updating: false,
      lastUpdatedAt: Date.now(),
    }),
    error: (s: ClassesState) => ({ ...s, updating: false, mutationError: true }),
  },
  {
    name: DELETE_CLASS_USE_CASE,
    executing: (s: ClassesState) => ({
      ...s,
      updating: true,
      mutationError: false,
      lastDeletedAt: null,
    }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      detail: s.detailRequest === a.meta?.input ? null : s.detail,
      updating: false,
      lastDeletedAt: Date.now(),
    }),
    error: (s: ClassesState) => ({ ...s, updating: false, mutationError: true }),
  },
  {
    name: GET_CLASS_DASHBOARD_ASSIGNMENTS_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      dashboardRequest: JSON.stringify(a.payload),
      dashboardAssignmentsLoading: true,
      dashboardAssignmentsError: null,
      ...(a.payload.nextToken ||
      dashboardScope(JSON.parse(s.dashboardRequest ?? '{}')) === dashboardScope(a.payload)
        ? {}
        : { dashboardAssignments: [], dashboardAssignmentsNextToken: null }),
    }),
    success: (s: ClassesState, a: any) =>
      s.dashboardRequest !== JSON.stringify(a.meta?.input)
        ? s
        : {
            ...s,
            dashboardAssignments: a.payload.data,
            dashboardAssignmentsNextToken: a.payload.nextToken,
            dashboardAssignmentsLoading: false,
            dashboardAssignmentsError: null,
          },
    error: (s: ClassesState, a: any) =>
      s.dashboardRequest !== JSON.stringify(a.meta?.input)
        ? s
        : { ...s, dashboardAssignmentsLoading: false, dashboardAssignmentsError: 'assignments' },
  },
  {
    name: LIST_ASSIGNMENT_CLASSES_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      assignmentOptionsLoading: true,
      assignmentOptionsError: null,
      ...(a.payload?.nextToken ? {} : { assignmentOptions: [], assignmentOptionsNextToken: null }),
    }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      assignmentOptions: [
        ...new Map(
          [...s.assignmentOptions, ...a.payload.items].map((item) => [item.id, item]),
        ).values(),
      ],
      assignmentOptionsNextToken: a.payload.nextToken,
      assignmentOptionsLoading: false,
    }),
    error: (s: ClassesState, a: any) => ({
      ...s,
      assignmentOptionsLoading: false,
      assignmentOptionsNextToken: null,
      assignmentOptionsError: String(
        a.error?.error?.message ?? a.error?.error ?? 'Could not load classes.',
      ),
    }),
  },
  {
    name: LIST_CLASSES_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      loading: true,
      listError: null,
      listRequest: JSON.stringify(a.payload),
      ...(a.payload?.nextToken ? {} : { items: [], nextToken: null }),
    }),
    success: (s: ClassesState, a: any) =>
      s.listRequest !== JSON.stringify(a.meta?.input)
        ? s
        : {
            ...s,
            items: a.meta?.input?.nextToken
              ? [
                  ...new Map(
                    [...s.items, ...a.payload.items].map((item) => [item.id, item]),
                  ).values(),
                ]
              : a.payload.items,
            nextToken: a.payload.nextToken,
            loading: false,
          },
    error: (s: ClassesState, a: any) =>
      s.listRequest !== JSON.stringify(a.meta?.input)
        ? s
        : { ...s, loading: false, listError: 'list' },
  },
  {
    name: CREATE_CLASS_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, creating: true, mutationError: false }),
    success: (s: ClassesState) => ({ ...s, creating: false, lastCreatedAt: Date.now() }),
    error: (s: ClassesState) => ({ ...s, creating: false, mutationError: true }),
  },
  {
    name: GET_CLASS_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      detailRequest: a.payload,
      ...(s.detailRequest !== a.payload
        ? {
            teachers: [],
            students: [],
            teachersNextToken: null,
            studentsNextToken: null,
            teacherRequest: null,
            studentRequest: null,
            teachersLoading: false,
            studentsLoading: false,
          }
        : {}),
      detail: s.detail?.id === a.payload ? s.detail : null,
      detailLoading: true,
      detailError: null,
    }),
    success: (s: ClassesState, a: any) =>
      s.detailRequest !== a.meta?.input ? s : { ...s, detail: a.payload, detailLoading: false },
    error: (s: ClassesState, a: any) =>
      s.detailRequest !== a.meta?.input ? s : { ...s, detailLoading: false, detailError: 'detail' },
  },
  {
    name: LIST_CLASS_TEACHERS_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      teacherRequest: JSON.stringify(a.payload),
      teachersLoading: true,
      rosterError: null,
      ...(a.payload.nextToken ? {} : { teachers: [], teachersNextToken: null }),
    }),
    success: (s: ClassesState, a: any) =>
      s.teacherRequest !== JSON.stringify(a.meta?.input)
        ? s
        : {
            ...s,
            teachersLoading: false,
            teachers: a.meta?.input?.nextToken
              ? [
                  ...new Map(
                    [...s.teachers, ...a.payload.items].map((item) => [item.teacherId, item]),
                  ).values(),
                ]
              : a.payload.items,
            teachersNextToken: a.payload.nextToken,
          },
    error: (s: ClassesState, a: any) =>
      s.teacherRequest !== JSON.stringify(a.meta?.input)
        ? s
        : { ...s, teachersLoading: false, rosterError: 'members' },
  },
  {
    name: LIST_CLASS_STUDENTS_USE_CASE,
    executing: (s: ClassesState, a: any) => ({
      ...s,
      studentRequest: JSON.stringify(a.payload),
      studentsLoading: true,
      rosterError: null,
      ...(a.payload.nextToken ? {} : { students: [], studentsNextToken: null }),
    }),
    success: (s: ClassesState, a: any) =>
      s.studentRequest !== JSON.stringify(a.meta?.input)
        ? s
        : {
            ...s,
            studentsLoading: false,
            students: a.meta?.input?.nextToken
              ? [
                  ...new Map(
                    [...s.students, ...a.payload.items].map((item) => [item.studentId, item]),
                  ).values(),
                ]
              : a.payload.items,
            studentsNextToken: a.payload.nextToken,
          },
    error: (s: ClassesState, a: any) =>
      s.studentRequest !== JSON.stringify(a.meta?.input)
        ? s
        : { ...s, studentsLoading: false, rosterError: 'members' },
  },
  {
    name: GET_USERS_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, memberProfilesLoading: true }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      memberProfiles: [
        ...new Map(
          [...s.memberProfiles, ...a.payload].map((item) => [item.id.toString(), item]),
        ).values(),
      ],
      memberProfilesLoading: false,
    }),
    error: (s: ClassesState) => ({ ...s, memberProfilesLoading: false }),
  },
  {
    name: UPDATE_CLASS_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, updating: true, mutationError: false }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      detail: s.detailRequest === a.payload.id ? a.payload : s.detail,
      updating: false,
      lastUpdatedAt: Date.now(),
    }),
    error: (s: ClassesState) => ({ ...s, updating: false, mutationError: true }),
  },
  {
    name: ARCHIVE_CLASS_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, updating: true, mutationError: false }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      detail: s.detailRequest === a.payload.id ? a.payload : s.detail,
      updating: false,
      lastUpdatedAt: Date.now(),
    }),
    error: (s: ClassesState) => ({ ...s, updating: false, mutationError: true }),
  },
  {
    name: REMOVE_CLASS_TEACHER_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, updating: true }),
    success: (s: ClassesState) => ({ ...s, updating: false, rosterVersion: s.rosterVersion + 1 }),
    error: (s: ClassesState) => ({ ...s, updating: false }),
  },
  {
    name: REMOVE_CLASS_STUDENT_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, updating: true }),
    success: (s: ClassesState) => ({ ...s, updating: false, rosterVersion: s.rosterVersion + 1 }),
    error: (s: ClassesState) => ({ ...s, updating: false }),
  },
  {
    name: GET_CLASS_INVITATION_BY_CODE_USE_CASE,
    executing: (s: ClassesState) => ({
      ...s,
      invitationByCode: null,
      invitationByCodeLoading: true,
    }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      invitationByCode: a.payload,
      invitationByCodeLoading: false,
    }),
    error: (s: ClassesState) => ({ ...s, invitationByCodeLoading: false }),
  },
  {
    name: GET_CLASS_INVITATION_USE_CASE,
    executing: (s: ClassesState) => ({
      ...s,
      invitation: null,
      invitationLoading: true,
      lastInvitationJoinedAt: null,
    }),
    success: (s: ClassesState, a: any) => ({
      ...s,
      invitation: a.payload,
      invitationLoading: false,
    }),
    error: (s: ClassesState) => ({ ...s, invitationLoading: false }),
  },
  {
    name: JOIN_CLASS_AS_TEACHER_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, joiningInvitation: true }),
    success: (s: ClassesState) => ({
      ...s,
      joiningInvitation: false,
      lastInvitationJoinedAt: Date.now(),
    }),
    error: (s: ClassesState) => ({ ...s, joiningInvitation: false }),
  },
  {
    name: LEAVE_CLASS_USE_CASE,
    executing: (s: ClassesState) => ({ ...s, leaving: true, lastLeftAt: null }),
    success: (s: ClassesState) => ({ ...s, leaving: false, lastLeftAt: Date.now() }),
    error: (s: ClassesState) => ({ ...s, leaving: false }),
  },
]);
